import { Link } from "react-router";
import { Fragment, type ReactNode } from "react";
import type { Sort, SortValue } from "@/lib/sortRows";
import styles from "./DataTable.module.css";
import { ChevronIcon } from "./Icon";
import { InfoTip } from "./InfoTip";

export type DataTableColumn<TRow> = {
  key: string;
  label: string;
  info?: string;
  // Prose columns wrap; everything else stays on one line so short values are
  // not broken up by a long neighbour.
  isProse?: boolean;
  // A sortable column reduces its row to one comparable value — the rendered
  // cell can be a whole component, and a badge does not compare. Supplying it
  // is what makes the header a control; sorting itself is the caller's, so a
  // listing that loads a page at a time can sort in its query instead.
  sortValue?: (row: TRow) => SortValue;
  render: (row: TRow) => ReactNode;
};

type DataTableProps<TRow> = {
  label: string;
  columns: DataTableColumn<TRow>[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  emptyMessage: string;
  draftRow?: ReactNode;
  // Trailing per-row controls. Supplying this adds one unlabelled column, so
  // rows that render their own cells must account for it in their colSpan.
  rowActions?: (row: TRow) => ReactNode;
  // The row whose key matches renders through renderEditRow instead of through
  // the columns — editing happens in place, not on a separate screen.
  editingKey?: string;
  renderEditRow?: (row: TRow) => ReactNode;
  // Rows whose children are showing. The panel spans every column, because a
  // child's columns are its own and borrowing the parent's would mislabel them.
  expandedKeys?: readonly string[];
  renderExpandedRow?: (row: TRow) => ReactNode;
  // The sort in force, and where a header click leads. Both are needed before
  // any header turns into a control: a sort link with nowhere to go would
  // reorder nothing.
  sort?: Sort;
  buildSortHref?: (key: string) => string;
};

// A plain semantic table: sorting rides in ordinary header links and there is
// no selection, so a role="grid" keyboard model would buy nothing and would
// fight the inputs the draft row puts in cells.
export const DataTable = <TRow,>({
  label,
  columns,
  rows,
  getRowKey,
  emptyMessage,
  draftRow,
  rowActions,
  editingKey,
  renderEditRow,
  expandedKeys,
  renderExpandedRow,
  sort,
  buildSortHref,
}: DataTableProps<TRow>) => {
  const hasActions = rowActions !== undefined;
  const columnCount = columns.length + (hasActions ? 1 : 0);
  const openKeys = new Set(expandedKeys ?? []);

  return (
    // Wide listings scroll inside their own box rather than pushing the page
    // sideways — the traits table is nine columns before its actions, and an
    // unreachable action column is the same as no action column.
    <div className={styles.scroller}>
      <table className={styles.table}>
        <caption className={styles.caption}>{label}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const isSortable =
                column.sortValue !== undefined && buildSortHref !== undefined;
              const isSorted = sort !== undefined && sort.key === column.key;

              return (
                <th
                  key={column.key}
                  scope="col"
                  className={styles.columnHeader}
                  aria-sort={
                    isSortable
                      ? isSorted
                        ? sort.isDescending
                          ? "descending"
                          : "ascending"
                        : "none"
                      : undefined
                  }
                >
                  {/* The tip stays outside the link: it is a button, and a
                    button inside an anchor is neither valid nor clickable. */}
                  <span className={styles.columnLabel}>
                    {isSortable ? (
                      // `replace` because the sort is one state, not a trail of
                      // history entries the back button has to walk out of.
                      <Link
                        to={buildSortHref(column.key)}
                        replace
                        preventScrollReset
                        className={styles.sortLink}
                        data-sorted={isSorted || undefined}
                        data-descending={
                          (isSorted && sort.isDescending) || undefined
                        }
                      >
                        {column.label}
                        <ChevronIcon size={12} />
                      </Link>
                    ) : (
                      column.label
                    )}
                    {column.info === undefined ? null : (
                      <InfoTip text={column.info} />
                    )}
                  </span>
                </th>
              );
            })}
            {hasActions ? (
              <th scope="col" className={styles.columnHeader}>
                <span className={styles.hiddenLabel}>Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {/* The draft row leads: a long listing would otherwise push it off the
            bottom of the page the moment it opens. */}
          {draftRow}
          {rows.length === 0 && draftRow === undefined ? (
            <tr>
              <td className={styles.empty} colSpan={columnCount}>
                {emptyMessage}
              </td>
            </tr>
          ) : null}
          {rows.map((row) => {
            const key = getRowKey(row);

            if (key === editingKey && renderEditRow !== undefined) {
              return <tr key={key}>{renderEditRow(row)}</tr>;
            }

            const isExpanded =
              openKeys.has(key) && renderExpandedRow !== undefined;

            return (
              // A fragment rather than two siblings in the map: the panel is a
              // second <tr>, and a table row may not nest inside another.
              <Fragment key={key}>
                <tr
                  className={styles.row}
                  data-expanded={isExpanded || undefined}
                >
                  {columns.map((column, index) =>
                    index === 0 ? (
                      <th
                        key={column.key}
                        scope="row"
                        className={styles.rowHeader}
                        data-prose={column.isProse || undefined}
                      >
                        {column.render(row)}
                      </th>
                    ) : (
                      <td
                        key={column.key}
                        className={styles.cell}
                        data-prose={column.isProse || undefined}
                      >
                        {column.render(row)}
                      </td>
                    ),
                  )}
                  {hasActions ? (
                    <td className={styles.actionsCell}>{rowActions(row)}</td>
                  ) : null}
                </tr>
                {isExpanded ? (
                  <tr>
                    <td className={styles.panelCell} colSpan={columnCount}>
                      {renderExpandedRow(row)}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
